import PropTypes from 'prop-types';
import { useRef } from 'react';
import styled from '@emotion/styled';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Accordion from '@base/Accordion/Accordion';
import useWindowSize from '@hooks/useWindowSize';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import OfferCard from './OfferCard';

const Flex = styled.div`
  display: flex;
`;

const FlexCenter = styled(Flex)`
  align-items: center;
`;

const OfferCardWrap = styled.div`
  width: ${({ $isFull }) => ($isFull ? '100%' : '50%')};
  margin: 0 0 ${Spacings.SPACING_8B} 0;
  &:last-child {
    margin-bottom: ${Spacings.SPACING_6B};
  }
  &:nth-last-child(-n + 2):nth-child(odd) {
    margin-bottom: ${Spacings.SPACING_6B};
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_2B};
  }
`;

const StyledAccordionOffers = styled(Accordion)`
  width: 100%;
  height: max-content;
  &.MuiAccordion-root {
    margin: 0;
    padding: 0;
    background-color: var(--color-base-0);
    box-shadow: none;
    &:hover {
      background-color: var(--color-shadow);
    }
    transition: background-color 200ms ease;
    ${mediaQuery.uptoTablet} {
      &:hover {
        background-color: var(--color-base-0);
      }
    }

    &.Mui-expanded {
      background-color: var(--color-base-0);
      transition: background-color 300ms ease;
    }
  }
`;

const StyledAccordionSummaryOffers = styled(Accordion.Summary)`
  min-height: inherit;
  &.MuiAccordionSummary-root {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
    ${mediaQuery.uptoTablet} {
      padding: ${Spacings.SPACING_4B} 0;
    }
    &.Mui-expanded {
      padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
      ${mediaQuery.uptoTablet} {
        padding: ${Spacings.SPACING_4B} 0;
      }
    }
  }
`;

const OffersWrap = styled.div`
  width: 100%;
`;

const AccDetailsWrap = styled(Flex)`
  flex-wrap: wrap;
`;

const GroupTitle = styled(H4)`
  line-height: 24px;
`;

const HeadRightCont = styled(FlexCenter)`
  margin-left: auto;
`;

const ExpandIconWrap = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
`;

const HorizontalLine = styled.div`
  position: absolute;
  top: 11px;
  left: 0;
  right: 0;
  margin: auto;
  border-radius: ${Spacings.SPACING_2};
  height: 2px;
  width: 14px;
  background-color: var(--color-text-secondary);
`;

const VerticalLine = styled(HorizontalLine)`
  top: 0;
  bottom: 0;
  height: 14px;
  width: 2px;
  opacity: ${({ $expanded }) => ($expanded ? 0 : 1)};
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  transition: transform 300ms ease, opacity 300ms ease;
`;

const CountTag = styled.div`
  margin-right: ${Spacings.SPACING_4B};
  height: 16px;
  background-color: var(--color-divider);
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_6};
  border-radius: ${Spacings.SPACING_1B};
`;

const CountTagLbl = styled(P1)`
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
`;

const OffersSection = ({ isExpanded, handleClick, titleTxt, offers }) => {
  const sectionRef = useRef();
  const { isMobile } = useWindowSize();
  const headerThreshold = isMobile ? 48 : 72;

  useFirstRenderSkip(() => {
    setTimeout(() => {
      const node = sectionRef.current;
      if (
        isExpanded &&
        node &&
        node.getBoundingClientRect().top < headerThreshold
      ) {
        window.scrollTo({
          top: node.offsetTop - headerThreshold,
          behavior: 'smooth'
        });
      }
    }, 500);
  }, [isExpanded]);

  return (
    <OffersWrap ref={sectionRef}>
      <StyledAccordionOffers expanded={isExpanded}>
        <StyledAccordionSummaryOffers onClick={handleClick}>
          <FlexCenter>
            <GroupTitle>{titleTxt}</GroupTitle>
            <HeadRightCont>
              <CountTag>
                <CountTagLbl color="var(--color-text-secondary)">
                  {offers.length}
                </CountTagLbl>
              </CountTag>
              <ExpandIconWrap>
                <HorizontalLine />
                <VerticalLine $expanded={isExpanded} />
              </ExpandIconWrap>
            </HeadRightCont>
          </FlexCenter>
        </StyledAccordionSummaryOffers>
        <Accordion.Details>
          <AccDetailsWrap>
            {offers.map((offer, offerIndex) => {
              return (
                <OfferCardWrap key={offerIndex.toString()}>
                  <OfferCard offer={offer} />
                </OfferCardWrap>
              );
            })}
          </AccDetailsWrap>
        </Accordion.Details>
      </StyledAccordionOffers>
    </OffersWrap>
  );
};

OffersSection.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  titleTxt: PropTypes.string.isRequired,
  offers: PropTypes.arrayOf(PropTypes.any).isRequired
};

export default OffersSection;
